import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  format, parseISO, isSameDay, isSameMonth,
  startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, addMonths, subMonths, startOfDay,
} from "date-fns";
import { fi as fiLocale, sv as svLocale } from "date-fns/locale";
import {
  Calendar as CalendarIcon, ChevronLeft, ChevronRight,
  Clock, X, CheckCircle2, XCircle, Lock,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";

const WEEK_OPTS = { weekStartsOn: 1 };

function StatusBadge({ status, t }) {
  const cls = {
    pending: "bg-[#B58A2F]/15 text-[#B58A2F] border-[#B58A2F]/40",
    accepted: "bg-red-100 text-red-700 border-red-300",
    declined: "bg-white text-[#1A1F1C]/50 border-[#E6E2D8] line-through",
    cancelled: "bg-white text-[#1A1F1C]/50 border-[#E6E2D8] line-through",
  }[status] || "";
  return <span className={`kav-chip text-[10px] ${cls}`}>{t(`status_${status}`) || status}</span>;
}

export default function MyBookings() {
  const { user, loading: authLoading } = useAuth();
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const dateLocale = lang === "fi" ? fiLocale : lang === "sv" ? svLocale : undefined;

  const [clientBookings, setClientBookings] = useState([]);   // bookings I made
  const [providerBookings, setProviderBookings] = useState([]); // bookings on my slots
  const [monthAnchor, setMonthAnchor] = useState(() => startOfMonth(new Date()));
  const [selectedDay, setSelectedDay] = useState(() => startOfDay(new Date()));

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const [cli, prov] = await Promise.all([
        api.get("/bookings/client").then(r => r.data).catch(() => []),
        api.get("/bookings/provider").then(r => r.data).catch(() => []),
      ]);
      setClientBookings(cli);
      setProviderBookings(prov);
    } catch (err) {
      console.error("[bookings] refresh failed", err);
    }
  }, [user]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { nav("/login"); return; }
    refresh();
  }, [user, authLoading, nav, refresh]);

  useEffect(() => {
    if (!user) return;
    const id = setInterval(refresh, 30_000);
    return () => clearInterval(id);
  }, [user, refresh]);

  const cancelClient = async (id) => {
    if (!window.confirm(t("cancel_booking_q"))) return;
    try {
      await api.patch(`/bookings/${id}/cancel`);
      toast.success(t("booking_cancelled"));
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error_generic"));
    }
  };

  const respond = async (id, status) => {
    try {
      await api.patch(`/bookings/${id}/status`, { status });
      toast.success(status === "accepted" ? t("request_accepted") : t("request_declined"));
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("error_generic"));
    }
  };

  const gridDays = useMemo(() => {
    const from = startOfWeek(startOfMonth(monthAnchor), WEEK_OPTS);
    const to = endOfWeek(endOfMonth(monthAnchor), WEEK_OPTS);
    return eachDayOfInterval({ start: from, end: to });
  }, [monthAnchor]);

  const dayKey = (d) => format(d, "yyyy-MM-dd");

  // Group all bookings (both roles) by day
  const byDay = useMemo(() => {
    const m = new Map();
    for (const b of clientBookings) {
      if (b.status === "declined" || b.status === "cancelled") continue;
      try {
        const key = format(parseISO(b.slot_start), "yyyy-MM-dd");
        if (!m.has(key)) m.set(key, []);
        m.get(key).push({ ...b, role: "client" });
      } catch (err) { console.error("[bookings] bad date", err); }
    }
    for (const b of providerBookings) {
      if (b.status === "declined" || b.status === "cancelled") continue;
      try {
        const key = format(parseISO(b.slot_start), "yyyy-MM-dd");
        if (!m.has(key)) m.set(key, []);
        m.get(key).push({ ...b, role: "provider" });
      } catch (err) { console.error("[bookings] bad date", err); }
    }
    return m;
  }, [clientBookings, providerBookings]);

  const daySelected = byDay.get(dayKey(selectedDay)) || [];
  const today = startOfDay(new Date());
  const monthLabel = format(monthAnchor, "LLLL yyyy", { locale: dateLocale });
  const localizedMonth = monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1);

  if (authLoading || !user) return null;

  return (
    <div data-testid="my-bookings-page" className="pt-24 pb-20">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <div className="overline text-[#143122]">{t("bookings")}</div>
        <h1 className="mt-2 font-serif-display text-4xl md:text-5xl text-[#143122]">
          {t("my_bookings")}
        </h1>
        <p className="mt-3 text-sm text-[#1A1F1C]/70 max-w-xl">
          {t("my_bookings_sub")}
        </p>

        {/* Month calendar */}
        <div className="mt-8 border border-[#E6E2D8] rounded-lg overflow-hidden bg-white">
          <div className="flex items-center justify-between px-4 py-3 bg-[#F4F1E9] border-b border-[#E6E2D8]">
            <button type="button" data-testid="mb-prev" onClick={() => setMonthAnchor(m => subMonths(m, 1))} className="p-1.5 rounded-md hover:bg-white">
              <ChevronLeft size={16} strokeWidth={1.5} />
            </button>
            <div data-testid="mb-month-label" className="font-serif-display text-lg text-[#143122]">{localizedMonth}</div>
            <div className="flex gap-1">
              <button type="button" data-testid="mb-today" onClick={() => { const t0 = startOfDay(new Date()); setMonthAnchor(startOfMonth(t0)); setSelectedDay(t0); }}
                className="text-xs px-2 py-1 rounded-md border border-[#143122]/30 text-[#143122] hover:bg-white">
                {t("today")}
              </button>
              <button type="button" data-testid="mb-next" onClick={() => setMonthAnchor(m => addMonths(m, 1))} className="p-1.5 rounded-md hover:bg-white">
                <ChevronRight size={16} strokeWidth={1.5} />
              </button>
            </div>
          </div>
          <div className="grid grid-cols-7 border-b border-[#E6E2D8]">
            {["mon", "tue", "wed", "thu", "fri", "sat", "sun"].map((wd, i) => (
              <div key={i} className="text-[10px] uppercase tracking-wider text-[#1A1F1C]/50 px-2 py-2 text-center border-r border-[#E6E2D8] last:border-r-0">
                {t(`wd_${wd}`)}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {gridDays.map((d, i) => {
              const key = dayKey(d);
              const inMonth = isSameMonth(d, monthAnchor);
              const isToday = isSameDay(d, today);
              const isSelected = isSameDay(d, selectedDay);
              const dBookings = byDay.get(key) || [];
              const pending = dBookings.filter(b => b.status === "pending").length;
              const accepted = dBookings.filter(b => b.status === "accepted").length;
              const isLastCol = (i + 1) % 7 === 0;
              const isLastRow = i >= gridDays.length - 7;
              return (
                <button
                  key={key}
                  type="button"
                  data-testid={`mb-day-${key}`}
                  onClick={() => setSelectedDay(d)}
                  className={`min-h-[68px] p-2 text-left transition-colors ${isLastCol ? "" : "border-r"} ${isLastRow ? "" : "border-b"} border-[#E6E2D8] ${
                    isSelected ? "bg-[#143122] text-white"
                      : inMonth ? "bg-white hover:bg-[#F4F1E9]"
                      : "bg-[#FAFAFA]/50 text-[#1A1F1C]/30 hover:bg-[#F4F1E9]"
                  }`}
                >
                  <div className={`text-sm font-medium ${isSelected ? "text-white" : isToday ? "text-[#143122] font-bold" : inMonth ? "text-[#1A1F1C]" : "text-[#1A1F1C]/30"}`}>
                    {format(d, "d")}
                  </div>
                  <div className="mt-1 space-y-0.5">
                    {accepted > 0 && (
                      <div className={`inline-block text-[10px] px-1.5 py-0.5 rounded ${isSelected ? "bg-red-500/30 text-white" : "bg-red-100 text-red-700"}`}>
                        {accepted} {t("booked_short")}
                      </div>
                    )}
                    {pending > 0 && (
                      <div className={`inline-block text-[10px] px-1.5 py-0.5 rounded ${isSelected ? "bg-[#B58A2F] text-white" : "bg-[#B58A2F]/15 text-[#B58A2F]"}`}>
                        {pending} {t("pending_short")}
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected day */}
        <div className="mt-6">
          <div className="flex items-center gap-2 text-sm font-medium text-[#143122]">
            <CalendarIcon size={14} strokeWidth={1.5} />
            {format(selectedDay, "EEEE d MMMM yyyy", { locale: dateLocale })}
          </div>
          <div className="mt-3">
            {daySelected.length === 0 ? (
              <div className="text-sm text-[#1A1F1C]/60 border border-dashed border-[#E6E2D8] rounded-lg py-8 text-center">
                {t("no_bookings_this_day")}
              </div>
            ) : (
              <ul className="grid sm:grid-cols-2 gap-2">
                {daySelected.map(b => {
                  const start = parseISO(b.slot_start);
                  const end = parseISO(b.slot_end);
                  const timeLabel = `${format(start, "HH:mm")} – ${format(end, "HH:mm")}`;
                  const isPending = b.status === "pending";
                  const isAccepted = b.status === "accepted";
                  const roleLabel = b.role === "provider" ? t("as_provider") : t("as_client");
                  const otherName = b.role === "provider" ? b.client_name : b.provider_name;

                  return (
                    <li key={`${b.role}-${b.id}`} data-testid={`booking-item-${b.id}`}>
                      <div className={`rounded-lg p-3 border ${
                        isAccepted ? "bg-red-50 border-red-300" :
                        isPending ? "bg-white border-[#B58A2F]/40" :
                        "bg-white border-[#E6E2D8]"
                      }`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className={`flex items-center gap-2 text-sm font-medium ${isAccepted ? "text-red-800" : "text-[#143122]"}`}>
                              {isAccepted ? <Lock size={12} strokeWidth={1.75} /> : <Clock size={12} strokeWidth={1.75} />} {timeLabel}
                            </div>
                            <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                              <span className="text-[10px] uppercase tracking-wider text-[#1A1F1C]/60">{roleLabel}</span>
                              <StatusBadge status={b.status} t={t} />
                            </div>
                            <div className="mt-1.5 text-sm text-[#1A1F1C]">
                              <Link to={`/users/${b.role === "provider" ? b.client_id : b.provider_id}`}
                                className="text-[#143122] hover:underline">
                                {otherName}
                              </Link>
                              {b.slot_title && <span className="text-[#1A1F1C]/70"> · {b.slot_title}</span>}
                            </div>
                            {(b.message || b.notes) && (
                              <div className="mt-1 text-xs text-[#1A1F1C]/70 italic">
                                &ldquo;{b.message || b.notes}&rdquo;
                              </div>
                            )}
                          </div>
                        </div>
                        {/* Provider actions on pending requests */}
                        {b.role === "provider" && isPending && (
                          <div className="mt-3 flex gap-2">
                            <button
                              type="button"
                              data-testid={`mb-accept-${b.id}`}
                              onClick={() => respond(b.id, "accepted")}
                              className="text-xs px-3 py-1 rounded bg-[#143122] text-white hover:bg-[#0f2418] inline-flex items-center gap-1"
                            >
                              <CheckCircle2 size={12} strokeWidth={1.75} /> {t("accept")}
                            </button>
                            <button
                              type="button"
                              data-testid={`mb-decline-${b.id}`}
                              onClick={() => respond(b.id, "declined")}
                              className="text-xs px-3 py-1 rounded border border-[#7A2A2A] text-[#7A2A2A] hover:bg-[#7A2A2A] hover:text-white inline-flex items-center gap-1"
                            >
                              <XCircle size={12} strokeWidth={1.75} /> {t("decline")}
                            </button>
                          </div>
                        )}
                        {/* Client actions: cancel pending or accepted */}
                        {b.role === "client" && (isPending || isAccepted) && (
                          <div className="mt-3">
                            <button
                              type="button"
                              data-testid={`mb-cancel-${b.id}`}
                              onClick={() => cancelClient(b.id)}
                              className="text-xs px-3 py-1 rounded border border-[#7A2A2A] text-[#7A2A2A] hover:bg-[#7A2A2A] hover:text-white inline-flex items-center gap-1"
                            >
                              <X size={12} strokeWidth={1.75} /> {t("cancel")}
                            </button>
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
